import { fetchVendorDetailsAndSetVendor } from "@bcc/routes/admin-roasters-page.controller";
import { getNavigationTarget } from "@bcc/routes/admin-roasters-page.utils";
import useVendorContext from "@client-shared/providers/vendor";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useNavigate } from "react-router-dom";

interface UseAdminImpersonationResult {
  handleImpersonate: (vendorKey: string) => Promise<void>;
  isSelectingVendor: boolean;
  selectionError: string | undefined;
}

/**
 * Encapsulates the admin impersonation side-effect flow:
 * 1. Clear query cache
 * 2. Clear vendor state
 * 3. Fetch vendor details and set vendor context
 * 4. Navigate to dashboard
 *
 * Keeps the page component free of orchestration logic.
 */
export function useAdminImpersonation(): UseAdminImpersonationResult {
  const navigate = useNavigate();
  const [, vendorDispatch, vendorUtils] = useVendorContext();
  const queryClient = useQueryClient();
  const [selectionError, setSelectionError] = React.useState<string | undefined>(undefined);
  const [isSelectingVendor, setIsSelectingVendor] = React.useState(false);

  const handleImpersonate = React.useCallback(
    async (vendorKey: string) => {
      setSelectionError(undefined);
      setIsSelectingVendor(true);
      try {
        queryClient.clear();
        vendorUtils?.clearVendorState();
        await fetchVendorDetailsAndSetVendor(vendorKey, vendorDispatch);
        navigate(getNavigationTarget());
      } catch (selectionFetchError) {
        const errorMessage = selectionFetchError instanceof Error ? selectionFetchError.message : "Unknown error";
        setSelectionError(errorMessage);
      } finally {
        setIsSelectingVendor(false);
      }
    },
    [navigate, vendorDispatch, vendorUtils, queryClient]
  );

  return { handleImpersonate, isSelectingVendor, selectionError };
}
